import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Moon, Sun } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { useTheme } from "../lib/theme";
import Logo from "../components/Logo";
import { LOGIN_BACKGROUND } from "../lib/constants";

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, refresh } = useAuth();
  const { settings } = useSettings();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null); // null loading, false invalid
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setInvite(false); setErr("No invite token provided."); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setInvite(data);
      } catch (e) {
        setInvite(false);
        setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [token]);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) return setErr("Password must be at least 8 characters.");
    if (password !== confirm) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/activate", { token, password });
      await refresh();
      navigate("/portal");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const isLight = theme === "light";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-[color:var(--atech-bg)] transition-colors duration-500">
      <div className="flex flex-col px-8 sm:px-16 py-10 lg:py-14">
        <div className="flex items-center justify-between">
          <Logo size={40} />
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            className="flex items-center gap-2 text-xs px-3 py-2 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-border-strong)] transition-colors"
            data-testid="theme-toggle"
          >
            {isLight ? <Moon size={14} /> : <Sun size={14} />}
            <span className="tracking-[0.16em] uppercase">{isLight ? "Dark" : "Light"}</span>
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-xl mt-10 lg:mt-0 fade-up">
          <div className="label text-[color:var(--atech-copper)] mb-5">Activate your account</div>

          {invite === null ? (
            <div className="label text-[color:var(--atech-text-muted)]">Checking your invite…</div>
          ) : invite === false ? (
            <div data-testid="invite-invalid">
              <h1 className="font-display text-4xl text-[color:var(--atech-text)] tracking-tight">
                This invite can't be used.
              </h1>
              <p className="mt-5 text-sm text-[color:var(--atech-text-dim)] leading-relaxed max-w-md">
                {err || "Please ask the A-Tech team to send you a fresh invite."}
              </p>
              <Link to="/login" className="btn-copper inline-flex items-center gap-2 text-sm mt-8">
                Back to sign-in <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-[2.4rem] sm:text-[2.8rem] leading-[1.1] tracking-tight text-[color:var(--atech-text)]">
                Welcome, {invite.name?.split(" ")[0] || invite.email}.
              </h1>
              <p className="mt-6 text-sm text-[color:var(--atech-text-dim)] leading-relaxed max-w-md">
                {invite.client_name
                  ? `You've been invited to the A-Tech Client Portal on behalf of ${invite.client_name}.`
                  : "You've been invited to the A-Tech Client Portal."}{" "}
                Set a password below to activate your account. You'll be signed in immediately after.
              </p>

              <form onSubmit={submit} className="mt-10 space-y-5 max-w-md" data-testid="activate-form">
                <div>
                  <span className="label block mb-2">Email</span>
                  <div className="text-sm text-[color:var(--atech-text)] font-mono" data-testid="activate-email">{invite.email}</div>
                </div>
                <div>
                  <label htmlFor="pw" className="label block mb-2">New password</label>
                  <input
                    id="pw" type="password" autoFocus required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[color:var(--atech-surface)] border border-[color:var(--atech-border)] focus:border-[color:var(--atech-copper)] focus:outline-none focus:ring-1 focus:ring-[color:var(--atech-copper)] rounded-[2px] px-4 py-3 text-[color:var(--atech-text)] placeholder:text-[color:var(--atech-text-muted)]"
                    placeholder="At least 8 characters"
                    data-testid="activate-password"
                  />
                </div>
                <div>
                  <label htmlFor="cp" className="label block mb-2">Confirm password</label>
                  <input
                    id="cp" type="password" required
                    value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-[color:var(--atech-surface)] border border-[color:var(--atech-border)] focus:border-[color:var(--atech-copper)] focus:outline-none focus:ring-1 focus:ring-[color:var(--atech-copper)] rounded-[2px] px-4 py-3 text-[color:var(--atech-text)] placeholder:text-[color:var(--atech-text-muted)]"
                    data-testid="activate-password-confirm"
                  />
                </div>
                {err && (
                  <div className="text-xs text-[#F87171] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.08)] rounded-[2px] px-3 py-2">{err}</div>
                )}
                <button type="submit" disabled={busy} className="btn-copper w-full flex items-center justify-center gap-2 disabled:opacity-60" data-testid="activate-submit">
                  {busy ? "Activating…" : "Activate & sign in"}
                  <CheckCircle2 size={16} />
                </button>
              </form>
            </>
          )}
        </div>

        <div className="mt-12 label text-[color:var(--atech-text-muted)]">
          {settings?.practice_name || "A-Tech"} · {new Date().getFullYear()}
        </div>
      </div>

      <div
        className="relative hidden lg:block atech-grain overflow-hidden"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(140deg, rgba(245,241,234,0.92) 0%, rgba(180,95,44,0.35) 55%, rgba(22,34,49,0.55) 100%), url('${LOGIN_BACKGROUND}')`
            : `linear-gradient(140deg, rgba(12,12,14,0.92) 0%, rgba(22,34,49,0.55) 60%, rgba(12,12,14,0.1) 100%), url('${LOGIN_BACKGROUND}')`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-x-12 bottom-14 z-10 max-w-md">
          <div className="label text-[color:var(--atech-copper)] mb-3">The portal</div>
          <div className="font-display text-3xl leading-tight tracking-tight text-white keep-white drop-shadow">
            {settings?.login_side_caption}
          </div>
        </div>
      </div>
    </div>
  );
}
